import { makeAutoObservable } from "mobx";
import { v4 } from "uuid";

import { DEFAULT_CHAT_TITLE } from "../constants";

import { ChatSession } from "./types";

export class SessionStore {
  chatSessions: ChatSession[] = [];
  currentChatId = "";

  constructor() {
    makeAutoObservable(this);

    const savedSessionId = sessionStorage.getItem("vibe-chat-last-session-id");
    if (!savedSessionId) {
      this.create();
    }
  }

  get currentSession(): ChatSession | undefined {
    return this.chatSessions.find(s => s.id === this.currentChatId);
  }

  create(): string {
    const newChat: ChatSession = {
      id: v4(),
      title: DEFAULT_CHAT_TITLE,
      createdAt: new Date(),
    };
    this.chatSessions = [newChat, ...this.chatSessions];
    this.currentChatId = newChat.id;
    return newChat.id;
  }

  select(id: string) {
    if (this.chatSessions.some(chat => chat.id === id)) {
      this.currentChatId = id;
    }
  }

  addSessionsFromRequest(sessions: ChatSession[]) {
    const existingIds = new Set(this.chatSessions.map(session => session.id));
    const newSessions = sessions.filter(session => !existingIds.has(session.id));
    if (newSessions.length > 0) {
      this.chatSessions = [...this.chatSessions, ...newSessions];
    }
  }

  updateTitle(chatId: string, title: string) {
    this.chatSessions = this.chatSessions.map(c => (c.id === chatId ? { ...c, title } : c));
  }

  updateName(sessionId: string, name: string) {
    this.chatSessions = this.chatSessions.map(c =>
      c.id === sessionId ? { ...c, title: name, isGeneratingName: false } : c,
    );
  }

  setNameGenerating(sessionId: string, isGenerating: boolean) {
    this.chatSessions = this.chatSessions.map(c =>
      c.id === sessionId ? { ...c, isGeneratingName: isGenerating } : c,
    );
  }

  updateNamePreview(sessionId: string, accumulatedName: string) {
    this.chatSessions = this.chatSessions.map(c =>
      c.id === sessionId ? { ...c, title: accumulatedName } : c,
    );
  }

  setSessions(sessions: ChatSession[]) {
    this.chatSessions = sessions;
  }

  remove(sessionId: string) {
    this.chatSessions = this.chatSessions.filter(c => c.id !== sessionId);
  }

  clearAll() {
    this.chatSessions = [];
    this.currentChatId = "";
  }

  setCurrentSessionId(sessionId: string) {
    this.chatSessions = this.chatSessions.map(c =>
      c.id === this.currentChatId ? { ...c, id: sessionId } : c,
    );
    this.currentChatId = sessionId;
  }

  renameSessionId(fromId: string, toId: string) {
    this.chatSessions = this.chatSessions.map(c => (c.id === fromId ? { ...c, id: toId } : c));
    if (this.currentChatId === fromId) {
      this.currentChatId = toId;
    }
  }
}
